import React, { useState, useMemo } from 'react';
import { 
  ChevronUp, 
  ChevronDown, 
  Search, 
  Filter, 
  Download,
  Award,
  TrendingUp
} from 'lucide-react';
import { ProcessedMember, TableSortConfig, FilterConfig } from '../types';
import { formatCurrency, formatNumber, downloadStyledExcel } from '../utils/helpers';
import { cn } from '../utils/helpers';

interface DataTableProps {
  members: ProcessedMember[];
  onExport?: () => void;
}

const DataTable: React.FC<DataTableProps> = ({ members, onExport }) => {
  const [sortConfig, setSortConfig] = useState<TableSortConfig>({
    key: 'finalScore',
    direction: 'desc'
  });
  
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({
    searchTerm: '',
    minScore: 0,
    maxScore: 100,
    showOnlyQualified: true
  });

  const [showFilters, setShowFilters] = useState(false);

  const handleSort = (key: keyof ProcessedMember) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredAndSortedMembers = useMemo(() => {
    let filtered = members.filter(member => {
      const matchesSearch = member.memberName.toLowerCase().includes(filterConfig.searchTerm.toLowerCase());
      const matchesScoreRange = member.finalScore >= filterConfig.minScore && member.finalScore <= filterConfig.maxScore;
      const matchesQualified = !filterConfig.showOnlyQualified || member.isQualified;
      
      return matchesSearch && matchesScoreRange && matchesQualified;
    });

    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });

    return filtered;
  }, [members, sortConfig, filterConfig]);

  // Experience grouping function
  const getExperienceGroup = (yearsInBNI: number): string => {
    if (yearsInBNI >= 7) return '7+';
    if (yearsInBNI >= 5) return '5-7';
    if (yearsInBNI >= 3) return '3-5';
    if (yearsInBNI >= 1) return '1-3';
    return '<1';
  };

  // Group members by experience
  const groupedMembers = useMemo(() => {
    const groups: { [key: string]: ProcessedMember[] } = {
      '7+': [],
      '5-7': [],
      '3-5': [],
      '1-3': [],
      '<1': []
    };

    filteredAndSortedMembers.forEach(member => {
      const group = getExperienceGroup(member.yearsInBNI);
      groups[group].push(member);
    });

    return groups;
  }, [filteredAndSortedMembers]);

  const handleExport = () => {
    // Create simple data array for the styled export
    const exportData = filteredAndSortedMembers.map(member => ({
      'Member Name': member.memberName,
      'Years in BNI': member.yearsInBNI,
      '121 Meetings': member.oneOnOneMeetings,
      'Ref Given': member.refGiven,
      'Ref Received': member.refReceived,
      'TYFCB Given': member.tyfcbGiven,
      'Revenue Received': member.revenueReceived,
      'Visitors': member.visitors,
      'Green Score': member.currScore || 0,
      'Contribution Gap': member.contributionGap,
      'Final Score': member.finalScore.toFixed(2),
      'Hot Seat Rank': filteredAndSortedMembers.indexOf(member) + 1
    }));
    
    downloadStyledExcel(exportData, 'bni-hot-seat-results-styled.xlsx', groupedMembers);
    onExport?.();
  };

  const SortableHeader: React.FC<{ 
    sortKey: keyof ProcessedMember; 
    children: React.ReactNode;
    className?: string;
  }> = ({ sortKey, children, className }) => (
    <th 
      className={cn("table-header cursor-pointer hover:bg-secondary-100 transition-colors duration-200", className)}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortConfig.key === sortKey && (
          sortConfig.direction === 'asc' ? 
            <ChevronUp className="w-4 h-4" /> : 
            <ChevronDown className="w-4 h-4" />
        )}
      </div>
    </th>
  );

  const ContributionBar: React.FC<{ member: ProcessedMember }> = ({ member }) => {
    const maxGap = Math.max(...members.map(m => m.contributionGap));
    const percentage = maxGap > 0 ? (member.contributionGap / maxGap) * 100 : 0;
    
    return (
      <div className="flex items-center space-x-2">
        <div className="w-16 h-2 bg-secondary-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-300"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-xs text-secondary-600 min-w-[60px]">
          {formatCurrency(member.contributionGap)}
        </span>
      </div>
    );
  };

  return (
    <div className="card overflow-hidden">
      {/* Table Header */}
      <div className="p-6 border-b border-secondary-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-secondary-900">
              Hot Seat Leaderboard
            </h3>
            <span className="px-2 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full">
              {filteredAndSortedMembers.length} members
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                "btn-secondary flex items-center space-x-2",
                showFilters && "bg-primary-100 text-primary-700"
              )}
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
            
            <button
              onClick={handleExport}
              className="btn-primary flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>
        
        {/* Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-secondary-50 rounded-lg space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Search Members
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
                  <input
                    type="text"
                    className="input-field pl-10"
                    placeholder="Search by name..."
                    value={filterConfig.searchTerm}
                    onChange={(e) => setFilterConfig(prev => ({ ...prev, searchTerm: e.target.value }))}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Min Score
                </label>
                <input
                  type="number"
                  className="input-field"
                  min="0"
                  max="100"
                  value={filterConfig.minScore}
                  onChange={(e) => setFilterConfig(prev => ({ ...prev, minScore: Number(e.target.value) }))}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Max Score
                </label>
                <input
                  type="number"
                  className="input-field"
                  min="0"
                  max="100"
                  value={filterConfig.maxScore}
                  onChange={(e) => setFilterConfig(prev => ({ ...prev, maxScore: Number(e.target.value) }))}
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="qualified-only"
                checked={filterConfig.showOnlyQualified}
                onChange={(e) => setFilterConfig(prev => ({ ...prev, showOnlyQualified: e.target.checked }))}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="qualified-only" className="text-sm text-secondary-700">
                Show only qualified members (Green Score &gt; 80 &amp; Revenue Received &lt; TYFCB Given)
              </label>
            </div>
          </div>
        )}
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="table-header w-12">#</th>
              <SortableHeader sortKey="memberName">Member Name</SortableHeader>
              <SortableHeader sortKey="yearsInBNI">Years in BNI</SortableHeader>
              <SortableHeader sortKey="oneOnOneMeetings">121 Meetings</SortableHeader>
              <SortableHeader sortKey="refGiven">Ref Given</SortableHeader>
              <SortableHeader sortKey="refReceived">Ref Received</SortableHeader>
              <SortableHeader sortKey="tyfcbGiven">TYFCB Given</SortableHeader>
              <SortableHeader sortKey="revenueReceived">Revenue Received</SortableHeader>
              <SortableHeader sortKey="visitors">Visitors</SortableHeader>
              <SortableHeader sortKey="currScore">Green Score</SortableHeader>
              <th className="table-header">Contribution Gap</th>
              <SortableHeader sortKey="finalScore">Final Score</SortableHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-200">
            {Object.entries(groupedMembers).map(([groupName, groupMembers]) => {
              if (groupMembers.length === 0) return null;
              
              return (
                <React.Fragment key={groupName}>
                  {/* Group Header */}
                  <tr className="bg-secondary-100">
                    <td colSpan={12} className="px-6 py-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-secondary-800">
                          {groupName} Years Experience ({groupMembers.length} members)
                        </h4>
                        <div className="text-xs text-secondary-600">
                          Avg Score: {(groupMembers.reduce((sum, m) => sum + m.finalScore, 0) / groupMembers.length).toFixed(1)}
                        </div>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Group Members */}
                  {groupMembers.map((member, groupIndex) => {
                    const globalIndex = filteredAndSortedMembers.indexOf(member);
                    return (
                      <tr 
                        key={member.sNo} 
                        className={cn(
                          "hover:bg-secondary-50 transition-colors duration-200",
                          globalIndex < 10 && "bg-primary-50"
                        )}
                      >
                        <td className="table-cell">
                          <div className="flex items-center space-x-2">
                            <span className="text-secondary-500">#{globalIndex + 1}</span>
                            {globalIndex < 10 && (
                              <Award className="w-4 h-4 text-primary-600" />
                            )}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-secondary-900">
                              {member.memberName}
                            </span>
                            {globalIndex < 10 && (
                              <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                                Hot Seat
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="table-cell">{member.yearsInBNI}</td>
                        <td className="table-cell">{formatNumber(member.oneOnOneMeetings)}</td>
                        <td className="table-cell">{formatNumber(member.refGiven)}</td>
                        <td className="table-cell">{formatNumber(member.refReceived)}</td>
                        <td className="table-cell">{formatCurrency(member.tyfcbGiven)}</td>
                        <td className="table-cell">{formatCurrency(member.revenueReceived)}</td>
                        <td className="table-cell">{formatNumber(member.visitors)}</td>
                        <td className="table-cell">
                          <span className={cn(
                            "font-medium",
                            (member.currScore || 0) >= 80 ? "text-success-600" :
                            (member.currScore || 0) >= 60 ? "text-primary-600" :
                            "text-secondary-600"
                          )}>
                            {(member.currScore || 0).toFixed(1)}
                          </span>
                        </td>
                        <td className="table-cell">
                          <ContributionBar member={member} />
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center space-x-2">
                            <span className={cn(
                              "font-semibold",
                              member.finalScore >= 80 ? "text-success-600" :
                              member.finalScore >= 60 ? "text-primary-600" :
                              "text-secondary-600"
                            )}>
                              {member.finalScore.toFixed(1)}
                            </span>
                            {member.finalScore >= 80 && (
                              <TrendingUp className="w-4 h-4 text-success-500" />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {filteredAndSortedMembers.length === 0 && (
        <div className="p-8 text-center">
          <div className="text-secondary-500">
            <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No members found</p>
            <p className="text-sm">Try adjusting your filters or search terms</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
